import type { EventEntityType } from '../events/event-types.js'

/**
 * Characters that widen a topic if they reach it from user-controlled data.
 * `#` and `+` are MQTT wildcards (remainder-of-topic and single-level), `*` is
 * an IAM policy wildcard, and `/` is the level separator, which lets a value
 * escape its own level.
 */
const TOPIC_WIDENING_CHARS = ['#', '+', '*', '/'] as const

/**
 * Wildcard leaf for the POLICY the authorizer grants.
 *
 * Two different matchers see this topic and they do not share a wildcard
 * vocabulary, so the grant and the subscription must use different characters:
 *
 * | leaf | MQTT broker (filter match) | IAM (`iot:Receive` ARN match) |
 * | ---- | -------------------------- | ----------------------------- |
 * | `#`  | multi-level wildcard       | literal — matches no topic    |
 * | `*`  | literal — matches no topic | wildcard                      |
 *
 * A grant becomes both an `iot:Subscribe` statement on `topicfilter/<topic>` and
 * an `iot:Receive` statement on `topic/<topic>`, and `iot:Receive` is evaluated
 * per message against the CONCRETE topic. So the policy needs `*`; granting `#`
 * authorizes the subscription and then denies every delivery.
 *
 * Use {@link REALTIME_SUBSCRIBE_LEAF} for the client's subscription filter.
 */
export const REALTIME_ENTITY_TYPE_ALL = '*'

/**
 * Wildcard leaf a CLIENT subscribes with.
 *
 * MQTT defines exactly two wildcards, `+` and `#`; `*` is a literal character in
 * a topic filter. Subscribing with `*` therefore matches only a topic whose last
 * level is literally `*` — the subscription is accepted and no message ever
 * arrives. The policy's `*` covers this filter because IAM reads `*` as a
 * wildcard, so the two agree without being the same string.
 */
export const REALTIME_SUBSCRIBE_LEAF = '#'

/** Topic leaf: one entity type, or a wildcard covering all of them. */
export type RealtimeEntityTypeSegment =
	| EventEntityType
	| typeof REALTIME_ENTITY_TYPE_ALL
	| typeof REALTIME_SUBSCRIBE_LEAF

export interface BuildUserTopicOptions {
	app: string
	stage: string
	userId: string
	entityType: RealtimeEntityTypeSegment
}

/**
 * Builds the per-user realtime topic, shared by the connection authorizer that
 * grants it and the worker that publishes to it so the two cannot disagree on
 * topic shape.
 *
 * The `{app}/{stage}` prefix is the sole isolation boundary between deployments,
 * because the underlying IoT endpoint is account-wide: one endpoint per region
 * per AWS account, shared by every app and stage. The prefix must therefore come
 * from the deployment's own configuration and never from client input.
 *
 * Throws rather than sanitizing a `userId` carrying `#`, `+`, or `/`: a
 * sanitized id would still build a *valid* topic, silently granting or
 * publishing to the wrong subtree, whereas a throw fails the connection closed
 * and is impossible to miss. A widened grant is the one outcome that must never
 * be reachable, and no legitimate user id contains these characters.
 *
 * @param {BuildUserTopicOptions} options - App and stage namespace, the resolved user's id, and the entity-type leaf.
 * @returns {string} The topic `{app}/{stage}/user/{userId}/{entityType}`.
 * @throws {Error} When `userId` is empty or contains an MQTT wildcard or level separator.
 */
export function buildUserTopic({ app, stage, userId, entityType }: BuildUserTopicOptions): string {
	assertSafeUserId(userId)
	return `${app}/${stage}/user/${userId}/${entityType}`
}

/**
 * Guards the only topic segment fed from a resolved database value rather than
 * from deployment configuration. `entityType` is exempt because the wildcard leaf
 * is a legitimate value there — the authorizer grants the whole subtree with it.
 *
 * @param {string} userId - Resolved ATLAS user id to embed in the topic.
 * @throws {Error} When the id is empty or would widen the topic.
 */
function assertSafeUserId(userId: string): void {
	if (userId.length === 0) {
		throw new Error('Realtime topic userId must not be empty')
	}

	const offending = TOPIC_WIDENING_CHARS.find((char) => userId.includes(char))
	if (offending !== undefined) {
		throw new Error(`Realtime topic userId must not contain "${offending}"`)
	}
}
